import { useEffect } from 'react';
import ModalPortal from '../modal/ModalPortal';
import type { PreviewKind } from '../../utils/filePreview';
import './DocumentPreviewModal.css';

type Props = {
  title: string;
  objectUrl: string | null;
  kind: PreviewKind;
  loading: boolean;
  error?: string | null;
  fileIndex?: number;
  fileCount?: number;
  onPrev?: () => void;
  onNext?: () => void;
  onDownload: () => void;
  onClose: () => void;
};

const DocumentPreviewModal = ({
  title,
  objectUrl,
  kind,
  loading,
  error,
  fileIndex = 0,
  fileCount = 1,
  onPrev,
  onNext,
  onDownload,
  onClose,
}: Props) => {
  const hasNav = fileCount > 1;
  const canPrev = hasNav && fileIndex > 0;
  const canNext = hasNav && fileIndex < fileCount - 1;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft' && canPrev) onPrev?.();
      if (event.key === 'ArrowRight' && canNext) onNext?.();
    };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose, onPrev, onNext, canPrev, canNext]);

  return (
    <ModalPortal>
      <div
        className="doc-preview-overlay"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="doc-preview-stage-wrap">
          {hasNav ? (
            <button
              type="button"
              className="doc-preview-nav"
              onClick={onPrev}
              disabled={!canPrev || loading}
              aria-label="Предыдущий файл"
            >
              ‹
            </button>
          ) : null}
          <div className="doc-preview-stage">
            {loading ? (
              <p className="doc-preview-status">Загрузка…</p>
            ) : error ? (
              <p className="doc-preview-status">{error}</p>
            ) : kind === 'image' && objectUrl ? (
              <img src={objectUrl} alt={title} />
            ) : kind === 'pdf' && objectUrl ? (
              <iframe title={title} src={objectUrl} />
            ) : (
              <p className="doc-preview-status">
                Этот файл нельзя показать здесь. Скачайте его, чтобы открыть.
              </p>
            )}
          </div>
          {hasNav ? (
            <button
              type="button"
              className="doc-preview-nav"
              onClick={onNext}
              disabled={!canNext || loading}
              aria-label="Следующий файл"
            >
              ›
            </button>
          ) : null}
        </div>
        {hasNav ? (
          <p className="doc-preview-counter">
            {fileIndex + 1} / {fileCount}
          </p>
        ) : null}
        <div className="doc-preview-actions">
          <button
            type="button"
            className="doc-preview-btn"
            onClick={onDownload}
            disabled={loading}
          >
            Скачать
          </button>
          <button type="button" className="doc-preview-btn" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default DocumentPreviewModal;
